import type { OpcionSelect } from "@/components";
import type { CondicionIva, Etiqueta, Proveedor, ResultadoBaja, Rubro } from "./api";
import { sinTildes } from "./buscar";
import { formatCuit } from "./fideicomisos";

export const ETIQUETA_IVA: Record<CondicionIva, string> = {
  responsable_inscripto: "Responsable inscripto",
  monotributo: "Monotributo",
  exento: "Exento",
  no_alcanzado: "No alcanzado",
};
export const OPCIONES_IVA: OpcionSelect[] = (Object.keys(ETIQUETA_IVA) as CondicionIva[]).map((c) => ({ valor: c, etiqueta: ETIQUETA_IVA[c] }));

export const TODOS_LOS_RUBROS = "todos";
export const TODAS_LAS_ETIQUETAS = "todas";
/** Opción "ninguno" de los Select del formulario (Radix no admite value=""). */
export const SIN_VALOR = "ninguno";

const digitos = (s: string) => s.replace(/\D/g, "");
/** Sólo se intenta la búsqueda por CUIT o teléfono cuando el texto es todo números y separadores (si no, "9 de Julio"
 * matchearía por el 9 suelto). Se aceptan `+` y paréntesis por los teléfonos ("+54 (11) ..."). */
const soloNumerico = (s: string) => /^[\d.\-\s+()]+$/.test(s);

export interface FiltroProveedores {
  texto: string;
  /** Nombre de la etiqueta por la que filtrar, o null para todas. */
  etiqueta: string | null;
  archivados: boolean;
}

export function filtrarProveedores(lista: Proveedor[], filtro: FiltroProveedores): Proveedor[] {
  const textoCrudo = filtro.texto.trim();
  const texto = sinTildes(textoCrudo);
  const numeros = soloNumerico(textoCrudo) ? digitos(textoCrudo) : "";
  const etiqueta = filtro.etiqueta === null ? null : claveEtiqueta(filtro.etiqueta);
  return lista.filter((p) => {
    if (!filtro.archivados && !p.activo) return false;
    if (etiqueta !== null && !p.etiquetas.some((e) => claveEtiqueta(e) === etiqueta)) return false;
    if (!texto) return true;
    if (sinTildes(p.nombre).includes(texto)) return true;
    if (p.etiquetas.some((e) => sinTildes(e).includes(texto))) return true;
    if (numeros.length === 0) return false;
    return (p.cuit ?? "").includes(numeros) || digitos(p.telefono ?? "").includes(numeros);
  });
}

export function opcionesFiltroEtiqueta(etiquetas: Etiqueta[]): OpcionSelect[] {
  return [{ valor: TODAS_LAS_ETIQUETAS, etiqueta: "Todas las etiquetas" }, ...etiquetas.map((e) => ({ valor: String(e.id), etiqueta: e.nombre }))];
}

// ------------------------------------------------------------------ etiquetas

export const MAX_ETIQUETAS = 20;
export const MAX_LARGO_ETIQUETA = 60;

/** Recorta y colapsa espacios internos. */
export const limpiarEtiqueta = (s: string) => s.trim().replace(/\s+/g, " ");
/** Clave para comparar etiquetas: sin mayúsculas ni tildes ("Demolición" = "demolicion"). */
export const claveEtiqueta = (s: string) => sinTildes(limpiarEtiqueta(s));

/** Agrega `texto` a las etiquetas del form. Si ya está (sin distinguir mayúsculas ni tildes) no la repite; si existe en
 * el `catalogo`, usa ese nombre (así "demolicion" se guarda como la "Demolición" que ya había). */
export function agregarEtiqueta(actuales: string[], texto: string, catalogo: string[] = []): { etiquetas: string[]; error: string | null } {
  const limpia = limpiarEtiqueta(texto);
  if (!limpia) return { etiquetas: actuales, error: null };
  if (limpia.length > MAX_LARGO_ETIQUETA) return { etiquetas: actuales, error: `Cada etiqueta tiene hasta ${MAX_LARGO_ETIQUETA} caracteres` };
  const clave = claveEtiqueta(limpia);
  if (actuales.some((e) => claveEtiqueta(e) === clave)) return { etiquetas: actuales, error: null };
  if (actuales.length >= MAX_ETIQUETAS) return { etiquetas: actuales, error: `Hasta ${MAX_ETIQUETAS} etiquetas por proveedor` };
  const nombre = catalogo.find((e) => claveEtiqueta(e) === clave) ?? limpia;
  return { etiquetas: [...actuales, nombre], error: null };
}

export const quitarEtiqueta = (actuales: string[], nombre: string) => actuales.filter((e) => e !== nombre);

/** Opciones del Combobox del form: las del catálogo que el proveedor todavía no tiene. */
export function opcionesEtiquetaForm(catalogo: Etiqueta[], actuales: string[]): OpcionSelect[] {
  const usadas = new Set(actuales.map(claveEtiqueta));
  return catalogo.filter((e) => !usadas.has(claveEtiqueta(e.nombre))).map((e) => ({ valor: e.nombre, etiqueta: e.nombre }));
}

/** Limpia, saca vacías y repetidas (sin distinguir mayúsculas ni tildes), conservando el orden. */
export function normalizarEtiquetas(etiquetas: string[]): string[] {
  const vistas = new Set<string>();
  const salida: string[] = [];
  for (const e of etiquetas) {
    const limpia = limpiarEtiqueta(e);
    const clave = claveEtiqueta(limpia);
    if (!limpia || vistas.has(clave)) continue;
    vistas.add(clave);
    salida.push(limpia);
  }
  return salida;
}

/** Rubros elegibles como habitual: los activos, más el actual aunque esté archivado. */
export function opcionesRubro(rubros: Rubro[], actualId?: number | null): OpcionSelect[] {
  return rubros
    .filter((r) => r.activo || r.id === actualId)
    .map((r) => ({ valor: String(r.id), etiqueta: r.activo ? r.nombre : `${r.nombre} (archivado)` }));
}

export function opcionesFiltroRubro(rubros: Pick<Rubro, "id" | "nombre">[]): OpcionSelect[] {
  return [{ valor: TODOS_LOS_RUBROS, etiqueta: "Todos los rubros" }, ...rubros.map((r) => ({ valor: String(r.id), etiqueta: r.nombre }))];
}

// ------------------------------------------------------------------ formulario

export interface FormProveedor {
  nombre: string;
  cuit: string;
  condicion_iva: string; // CondicionIva, "" o SIN_VALOR
  contacto: string;
  telefono: string;
  email: string;
  banco: string;
  cbu_alias: string;
  etiquetas: string[];
  notas: string;
  recomendado_por: string; // de parte de quién viene (Mati, 2026-10-19)
  validado: boolean; // ya trabajamos con él y damos fe
}

/** Campos de texto del form (todos menos `etiquetas` y `validado`). */
export type CampoTextoProveedor = Exclude<keyof FormProveedor, "etiquetas" | "validado">;

export type ErroresProveedor = Partial<Record<"nombre" | "cuit" | "email" | "cbu_alias" | "contacto" | "telefono" | "banco" | "notas" | "recomendado_por" | "etiquetas", string>>;

export interface PayloadProveedor {
  nombre: string;
  cuit: string | null;
  condicion_iva: CondicionIva | null;
  contacto: string | null;
  telefono: string | null;
  email: string | null;
  banco: string | null;
  cbu_alias: string | null;
  etiquetas: string[];
  notas: string | null;
  recomendado_por: string | null;
  validado: boolean;
}

export function formProveedor(p?: Proveedor): FormProveedor {
  return {
    nombre: p?.nombre ?? "",
    cuit: p?.cuit ? formatCuit(p.cuit) : "",
    condicion_iva: p?.condicion_iva ?? "",
    contacto: p?.contacto ?? "",
    telefono: p?.telefono ?? "",
    email: p?.email ?? "",
    banco: p?.banco ?? "",
    cbu_alias: p?.cbu_alias ?? "",
    etiquetas: [...(p?.etiquetas ?? [])],
    notas: p?.notas ?? "",
    recomendado_por: p?.recomendado_por ?? "",
    validado: p?.validado ?? false,
  };
}

const cuitDigitos = (cuit: string) => cuit.replace(/[.\-\s]/g, "");

/** Mismas reglas que app/proveedores/reglas.py (el backend es la autoridad; esto evita el viaje). */
export function validarProveedor(f: FormProveedor): ErroresProveedor {
  const errores: ErroresProveedor = {};
  if (!f.nombre.trim()) errores.nombre = "Poné el nombre";
  else if (f.nombre.trim().length > 160) errores.nombre = "Hasta 160 caracteres";
  const cuit = cuitDigitos(f.cuit);
  if (cuit) {
    if (!/^\d+$/.test(cuit)) errores.cuit = "El CUIT sólo puede tener números, puntos y guiones";
    else if (cuit.length !== 11) errores.cuit = "El CUIT tiene que tener 11 dígitos";
  }
  if (f.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) errores.email = "Revisá el email";
  const pago = f.cbu_alias.trim();
  if (pago) {
    if (/^\d+$/.test(pago)) {
      if (pago.length !== 22) errores.cbu_alias = "El CBU o CVU tiene que tener 22 dígitos";
    } else if (!/^[a-z0-9.-]{6,20}$/i.test(pago)) {
      errores.cbu_alias = "El alias tiene de 6 a 20 caracteres: letras, números, puntos y guiones";
    }
  }
  if (f.contacto.trim().length > 120) errores.contacto = "Hasta 120 caracteres";
  if (f.telefono.trim().length > 40) errores.telefono = "Hasta 40 caracteres";
  if (f.banco.trim().length > 80) errores.banco = "Hasta 80 caracteres";
  if (f.notas.trim().length > 2000) errores.notas = "Hasta 2000 caracteres";
  if (f.recomendado_por.trim().length > 120) errores.recomendado_por = "Hasta 120 caracteres";
  const etiquetas = normalizarEtiquetas(f.etiquetas);
  if (etiquetas.length > MAX_ETIQUETAS) errores.etiquetas = `Hasta ${MAX_ETIQUETAS} etiquetas por proveedor`;
  else if (etiquetas.some((e) => e.length > MAX_LARGO_ETIQUETA)) errores.etiquetas = `Cada etiqueta tiene hasta ${MAX_LARGO_ETIQUETA} caracteres`;
  return errores;
}

const vacioANull = (s: string) => (s.trim() === "" ? null : s.trim());

/** Cuerpo del POST/PATCH. Asume que `validarProveedor(f)` no devolvió errores. */
export function aPayloadProveedor(f: FormProveedor): PayloadProveedor {
  return {
    nombre: f.nombre.trim(),
    cuit: vacioANull(cuitDigitos(f.cuit)),
    condicion_iva: f.condicion_iva === "" || f.condicion_iva === SIN_VALOR ? null : (f.condicion_iva as CondicionIva),
    contacto: vacioANull(f.contacto),
    telefono: vacioANull(f.telefono),
    email: vacioANull(f.email),
    banco: vacioANull(f.banco),
    cbu_alias: vacioANull(f.cbu_alias),
    etiquetas: normalizarEtiquetas(f.etiquetas),
    notas: vacioANull(f.notas),
    recomendado_por: vacioANull(f.recomendado_por),
    validado: f.validado,
  };
}

// ------------------------------------------------------------------ rubros y bajas

export function validarNombreRubro(nombre: string): string | null {
  if (!nombre.trim()) return "Poné el nombre";
  if (nombre.trim().length > 80) return "Hasta 80 caracteres";
  return null;
}

export function mensajeBaja(resultado: ResultadoBaja["resultado"], nombre: string): string {
  return resultado === "borrado" ? `${nombre} se borró.` : `${nombre} ya se usó en gastos: quedó archivado.`;
}
